import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import type { TestOrderStatus } from '@/features/patient-record/types';

const STAGE_ORDER = ['ORDERED', 'SAMPLE_SENT', 'RESULTED', 'REVIEWED'] as const;
type StageStatus = (typeof STAGE_ORDER)[number];

const STAGE_INDEX: Record<StageStatus, number> = {
  ORDERED: 0,
  SAMPLE_SENT: 1,
  RESULTED: 2,
  REVIEWED: 3,
};

interface TestOrderTimelineProps {
  readonly status: TestOrderStatus;
}

/**
 * Compact stepper: a single dashed track from the first to the last dot,
 * with dots positioned at evenly spaced anchors and labels sitting flush
 * beneath each. Uses absolute positioning so dots align cleanly regardless
 * of how wide the surrounding card is.
 */
export function TestOrderTimeline({
  status,
}: TestOrderTimelineProps): ReactElement | null {
  const { t } = useTranslation();

  if (status === 'CANCELLED') return null;

  const activeIndex = STAGE_INDEX[status];
  const lastIndex = STAGE_ORDER.length - 1;
  const progressPct = lastIndex === 0 ? 0 : (activeIndex / lastIndex) * 100;

  return (
    <div className="px-1.5 pt-1 pb-0.5" aria-label={t('patient.testOrders.statusFieldLabel')}>
      <div className="relative h-2.5">
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px border-t border-dashed border-border" />
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 h-px border-t border-dashed border-primary/60"
          style={{ width: `${progressPct}%` }}
          aria-hidden="true"
        />
        {STAGE_ORDER.map((stage, idx) => {
          const isActive = idx === activeIndex;
          const isPast = idx < activeIndex;
          const left = idx === 0 ? 0 : (idx / lastIndex) * 100;
          return (
            <span
              key={stage}
              className={cn(
                'absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-2.5 w-2.5 rounded-full border-2',
                isActive && 'bg-primary border-primary',
                isPast && 'bg-primary/60 border-primary/60',
                !isActive && !isPast && 'bg-card border-border',
              )}
              style={{ left: `${left}%` }}
              aria-current={isActive ? 'step' : undefined}
            />
          );
        })}
      </div>
      <div className="relative mt-1.5 h-4">
        {STAGE_ORDER.map((stage, idx) => {
          const isActive = idx === activeIndex;
          const isPast = idx < activeIndex;
          const left = idx === 0 ? 0 : (idx / lastIndex) * 100;
          return (
            <span
              key={stage}
              className={cn(
                'absolute top-0 -translate-x-1/2 text-[11px] whitespace-nowrap',
                isActive && 'font-medium text-foreground',
                isPast && 'text-foreground',
                !isActive && !isPast && 'text-muted-foreground',
                idx === 0 && 'left-0 translate-x-0',
                idx === lastIndex && 'right-0 left-auto translate-x-0',
              )}
              style={
                idx === 0 || idx === lastIndex ? undefined : { left: `${left}%` }
              }
            >
              {t(`patient.testOrders.timeline.${stage}`)}
            </span>
          );
        })}
      </div>
    </div>
  );
}
